import { useState } from "react";

import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DatePicker } from "@/components/shared/date-picker";
import { SearchableSelect } from "@/components/shared/searchable-select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useHasPermission } from "@/hooks/use-auth";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNextNumberPreview } from "@/hooks/use-number-sequences";

import {
  RIDER_SALE_STATUS_BADGE_VARIANT,
  RIDER_SALE_STATUS_LABELS,
  RIDER_SALE_STATUS_NEXT,
} from "./rider-sale-labels";
import { RiderSaleLinesTable } from "./rider-sale-lines-table";
import { RiderSalePaymentsSection } from "./rider-sale-payments-section";
import { useRiderSaleForm } from "./use-rider-sale-form";

import type { RiderSale } from "@artisancode/api-types";

export function RiderSaleDialog({
  open,
  onOpenChange,
  editing,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: RiderSale | null;
}) {
  // Moving to done/paid is the settlement (setoran), gated by rider_sales.settle.
  const canSettle = useHasPermission("rider_sales.settle");
  const {
    form,
    fields,
    products,
    stockByProductId,
    riders,
    productQuery,
    setProductQuery,
    visibleIndexes,
    totalJual,
    totalKembali,
    totalSelisih,
    totalSetoran,
    paymentFields,
    addPayment,
    removePayment,
    methodOptionsFor,
    canAddPayment,
    isSubmitting,
    onSubmit,
  } = useRiderSaleForm(open, editing, () => onOpenChange(false));

  const { preview: sequencePreview, autoGenerate } =
    useNextNumberPreview("rider_sale");
  const previewSeq = !editing && autoGenerate ? sequencePreview : "";
  const isMobile = useIsMobile();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmStatusOpen, setConfirmStatusOpen] = useState(false);
  const title = editing ? "Edit Penjualan Rider" : "Tambah Penjualan Rider";

  const footer = (
    <>
      <Button
        variant="outline"
        onClick={() => onOpenChange(false)}
        disabled={isSubmitting}
      >
        Batal
      </Button>
      <Button type="submit" form="rider-sale-form" disabled={isSubmitting}>
        {isSubmitting ? "Menyimpan..." : editing ? "Simpan" : "Tambah"}
      </Button>
    </>
  );

  const formContent = (
    <Form {...form}>
      <form
        id="rider-sale-form"
        onSubmit={form.handleSubmit((values) => {
          if (!editing) setConfirmOpen(true);
          else if (values.status !== editing.status) setConfirmStatusOpen(true);
          else void onSubmit();
        })}
        className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto py-2 pr-1 md:grid-cols-[1.4fr_1fr]"
      >
        <div className="space-y-3 md:flex md:min-h-0 md:flex-col md:gap-3 md:space-y-0">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tanggal</FormLabel>
                  <FormControl>
                    <DatePicker value={field.value} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="riderId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Rider</FormLabel>
                  <FormControl>
                    <SearchableSelect
                      value={field.value}
                      onChange={field.onChange}
                      options={riders.map((r) => ({
                        value: r.id,
                        label: r.name,
                      }))}
                      placeholder="Pilih rider..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <RiderSaleLinesTable
            form={form}
            fields={fields}
            products={products}
            stockByProductId={stockByProductId}
            visibleIndexes={visibleIndexes}
            productQuery={productQuery}
            setProductQuery={setProductQuery}
            totalJual={totalJual}
            totalKembali={totalKembali}
            totalSelisih={totalSelisih}
            totalSetoran={totalSetoran}
          />
        </div>

        <div className="flex flex-col gap-3">
          <RiderSalePaymentsSection
            form={form}
            paymentFields={paymentFields}
            addPayment={addPayment}
            removePayment={removePayment}
            methodOptionsFor={methodOptionsFor}
            canAddPayment={canAddPayment}
          />

          <FormField
            control={form.control}
            name="status"
            render={({ field }) => {
              const currentStatus = editing?.status ?? "draft";
              const nextOptions =
                editing && canSettle
                  ? RIDER_SALE_STATUS_NEXT[currentStatus]
                  : [];
              return (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      variant={RIDER_SALE_STATUS_BADGE_VARIANT[currentStatus]}
                    >
                      {RIDER_SALE_STATUS_LABELS[currentStatus]}
                    </Badge>
                    {nextOptions.map((next) => (
                      <Button
                        key={next}
                        type="button"
                        size="sm"
                        variant={field.value === next ? "default" : "outline"}
                        onClick={() =>
                          field.onChange(
                            field.value === next ? currentStatus : next,
                          )
                        }
                      >
                        {field.value === next ? "Batal · " : "Tandai "}
                        {RIDER_SALE_STATUS_LABELS[next]}
                      </Button>
                    ))}
                  </div>
                  {!editing ? (
                    <p className="text-xs text-muted-foreground">
                      Transaksi baru selalu mulai dari Draft.
                    </p>
                  ) : (
                    nextOptions.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        Status ini sudah final — tidak ada aksi lanjutan.
                      </p>
                    )
                  )}
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="transactionCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>ID Transaksi</FormLabel>
                <FormControl>
                  <Input
                    disabled={!editing && autoGenerate}
                    placeholder={
                      editing
                        ? "Opsional"
                        : previewSeq
                          ? `Otomatis: ${previewSeq}`
                          : "Opsional"
                    }
                    {...field}
                  />
                </FormControl>
                {!editing && autoGenerate && (
                  <p className="text-xs text-muted-foreground">
                    Nomor transaksi selalu otomatis saat pertama dibuat.
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Keterangan</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Opsional"
                    className="min-h-[72px] resize-none"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Simpan penjualan rider?"
        description="Stok yang dibawa rider langsung dicatat keluar dan tercatat di Kartu Stok."
        confirmLabel="Ya, Simpan"
        variant="default"
        isConfirming={isSubmitting}
        onConfirm={async () => {
          await onSubmit();
          setConfirmOpen(false);
        }}
      />

      <ConfirmDialog
        open={confirmStatusOpen}
        onOpenChange={setConfirmStatusOpen}
        title="Tandai penjualan rider Done?"
        description="Hasil penjualan dikunci: stok terjual dan sisa yang kembali ke toko dicatat di Kartu Stok. Dokumen tidak bisa diedit lagi dan tidak bisa dikembalikan ke Draft."
        confirmLabel="Ya, Tandai Done"
        variant="default"
        isConfirming={isSubmitting}
        onConfirm={async () => {
          await onSubmit();
          setConfirmStatusOpen(false);
        }}
      />
    </Form>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="flex max-h-[92dvh] flex-col gap-0 overflow-hidden rounded-t-2xl p-4"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <SheetHeader className="p-0 pb-3 text-left">
            <SheetTitle>{title}</SheetTitle>
          </SheetHeader>
          {formContent}
          <SheetFooter className="flex-row justify-end gap-2 border-t p-0 pt-3">
            {footer}
          </SheetFooter>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92dvh] flex-col overflow-hidden p-4 sm:p-6 sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {formContent}
        <DialogFooter className="flex flex-row justify-end gap-2 border-t pt-3 mt-auto">
          {footer}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
